import React from "react";
import clsx from "clsx";
// import { createTimeline, Timeline } from "animejs";
import "./chat-box-list.styles.scss";
import zCBLAnimationModule from "../../core/engines";
import { FlipperProps } from "../../core/engines/Flipper/types";

import { $Logger } from "../../../logger";

import type { IUIMessageContent } from "../../shared/types";
import type { IChatBoxListUIController } from "../../core/controllers/chat-box-list-ui-controller";
import { ICBLAnimationNG } from "../../core/engines/cbl-animationNG";
import type { ChatBoxListState } from "../../core/controllers/chat-box-list-ui-controller";

import {
	isTextMessageContent,
	isPhotoMessageContent,
	isGroupPhotoMessageContent,
	isStickerMessageContent,
	isGroupStickerMessageContent,
	isFileMessageContent,
	isLinkMessageContent,
} from "../../shared/types";

import { TextMessage } from "../messages/TextMessage";

import { ChatBoxListItem } from "../ChatBoxListItem";
import { MessageLayout } from "../messages/MessageLayout";
import { StickerMessage } from "../messages/StickerMessage";
import { GroupStickerMessage } from "../messages/GroupStickerMessage";

export interface ChatBoxListProps {
	className?: string;
	scrollerElementId: string;
	toUidInfo: {
		uid: string;
		name: string;
		avatarUrl: string;
	};
	fromUidInfo: {
		uid: string;
		name: string;
		avatarUrl: string;
	};
	state: ChatBoxListState;
	controller: IChatBoxListUIController;
	animationNG: ICBLAnimationNG;
	containerRef: React.RefObject<HTMLDivElement | null>;
}

interface InternalChatBoxListState {}
interface ChatBoxListSnapshot {
	prevScrollHeight: number;
}

const BlockName = "chat-box-list";
const ClassNames = {
	Block: BlockName,
	Modifiers: {},
	Elements: {},
} as const;

/**
 * NOTE:
 * - Để play animation cho các message item thì ta phải sure 100% là phải có các view model -> phải có key riêng đoàng hoàng...
 * - Vẫn chưa có cái cơ chế để access các message item một các chuẩn chỉ...
 * - Ví dụ:
 * + Nếu chat box list là nơi create slide new message item animation thì lúc tạo là khi nào?
 * + Sau đó, các message item có các field, các logic bên trong để tự play animation để sync với scroll position hoặc sync với play animation timeline thì làm như thế nào?
 * + Proposal idea:
 * +
 */
export class ChatBoxList extends React.Component<
	ChatBoxListProps,
	InternalChatBoxListState,
	ChatBoxListSnapshot
> {
	private rafLor_: number | null = null;
	private AdvancedDebugOpts: FlipperProps["advancedDebugOpts"];

	constructor(props: ChatBoxListProps) {
		super(props);
		$Logger.warn("ChatBoxList call constructor");

		this.state = {};

		this.AdvancedDebugOpts = {
			reactLifecycle: {
				debugCtor: true,
				debugRender: true,
				debugDidMount: true,
				debugGetSS: true,
				debugDidUpdate: true,
				debugWillUnmount: true,
			},
		};
	}

	//#region React Event Handlers
	//#endregion React Event Handlers

	static getDerivedStateFromProps(props: ChatBoxListProps, state: InternalChatBoxListState) {
		$Logger.warn("ChatBoxList call getDerivedStateFromProps");

		return null;
	}

	public shouldComponentUpdate(
		nextProps: Readonly<ChatBoxListProps>,
		nextState: Readonly<InternalChatBoxListState>,
		nextContext: any,
	): boolean {
		$Logger.warn("ChatBoxList call shouldComponentUpdate");

		return true;
	}

	public render() {
		const { messages } = this.props.state;
		return (
			<zCBLAnimationModule.Component.Flipper
				id={this.props.controller.chatBoxListId}
				element={"div"}
				// flipIds={[]}
				//@ts-ignore
				flipIds={messages}
				animationNG={this.props.animationNG}
				shouldFlipOnMount={false}
				// advancedDebugOpts={this.AdvancedDebugOpts}
				className={clsx(ChatBoxList.ClassNames.Block, this.props.className)}
			>
				{(() => {
					let prevMessage: IUIMessageContent | null = null;
					return messages.map((message) => {
						const isFromMe = message.fromUid === this.props.fromUidInfo.uid;
						const itemLayoutType = isFromMe ? "Right" : "Left";
						const itemSpacing = (() => {
							if (!prevMessage) return 0;
							return prevMessage.fromUid === message.fromUid ? 4 : 8;
						})();

						const messageLayoutType =
							([isTextMessageContent].some((type) => type(message)) && "Frame") ||
							([isStickerMessageContent, isGroupStickerMessageContent].some((type) =>
								type(message),
							) &&
								"NoFrame") ||
							"Frame";

						prevMessage = message;
						return (
							<ChatBoxListItem
								key={`${message.toUid}-${message.cliMsgId}`}
								content={message}
								layoutType={itemLayoutType}
								spacing={itemSpacing}
							>
								<MessageLayout
									key={`${message.toUid}-${message.cliMsgId}`}
									content={message}
									//@ts-ignore
									layoutType={messageLayoutType}
									isFromMe={isFromMe}
								>
									{() => {
										if (isTextMessageContent(message)) {
											return <TextMessage content={message} />;
										}

										if (isStickerMessageContent(message)) {
											return <StickerMessage content={message} />;
										}

										if (isGroupStickerMessageContent(message)) {
											return <GroupStickerMessage content={message} />;
										}

										//TODO: return null or unknown message type.
										return null;
									}}
								</MessageLayout>
							</ChatBoxListItem>
						);
					});
				})()}
			</zCBLAnimationModule.Component.Flipper>
		);
	}

	public componentDidMount() {
		$Logger.info("ChatBoxList call componentDidMount");
		this.props.controller.initChatBoxList();
		// const didMountWithProps = this.props;
		// const didMountWithState = this.props.state;
		// const didMountWithAnimationScopeId = this.props.state.animationScopeId;

		// this.props.controller.animationNG.reactWithComponentDidMount(
		// 	didMountWithAnimationScopeId,
		// 	didMountWithProps,
		// 	didMountWithState,
		// );
	}

	public getSnapshotBeforeUpdate(
		prevProps: Readonly<ChatBoxListProps>,
		prevState: Readonly<InternalChatBoxListState>,
	): ChatBoxListSnapshot | null {
		$Logger.info("ChatBoxList call getSnapshotBeforeUpdate");

		// const getSnapshotBeforeUpdateWithProps = this.props;
		// const getSnapshotBeforeUpdateWithState = this.props.state;
		// const getSnapshotBeforeUpdateWithAnimationScopeId = this.props.state.animationScopeId;

		// this.props.controller.animationNG.reactWithGetSnapshotBeforeUpdate(
		// 	getSnapshotBeforeUpdateWithAnimationScopeId,
		// 	prevProps,
		// 	getSnapshotBeforeUpdateWithProps,
		// 	prevProps.state,
		// 	getSnapshotBeforeUpdateWithState,
		// );

		// if (true) {
		// 	const prevScrollHeight = this.props.containerRef.current?.scrollHeight ?? 0;
		// 	return {
		// 		prevScrollHeight,
		// 	};
		// }

		/**
		 * TODO:
		 * - Lúc get snapshot của các chat box list item và của cả message item mà có khả năng play animation thì.
		 * - Ban đầu, ta phải mark các item đó với data-animation-id-<cliMsgId>.
		 * - Để get được correct position thì ta nên decorate các message item với data-animation-id-<cliMsgId>.
		 * - Khi có message mới
		 */

		return null;
	}

	public componentDidUpdate(
		prevProps: Readonly<ChatBoxListProps>,
		prevState: Readonly<InternalChatBoxListState>,
		snapshot: ChatBoxListSnapshot,
	) {
		$Logger.info("ChatBoxList call componentDidUpdate");

		// const didUpdateWithProps = this.props;
		// const didUpdateWithState = this.props.state;
		// const didUpdateWithAnimationScopeId = this.props.state.animationScopeId;

		// this.props.controller.animationNG.reactWithComponentDidUpdate(
		// 	didUpdateWithAnimationScopeId,
		// 	prevProps,
		// 	didUpdateWithProps,
		// 	prevProps.state,
		// 	didUpdateWithState,
		// 	snapshot,
		// );

		/**
		 * TODO:
		 * - Call anime engine to animate the chat box list item.
		 * - Use animejs to animate the chat box list item.
		 * - Sync animation with the scroll position of the chat box list.
		 */
		// const lastMessageInList = this.props.state.messages[this.props.state.messages.length - 1];
		// const chatBoxListItem = window.document.getElementById(lastMessageInList.cliMsgId);
		// if (chatBoxListItem) {
		// }

		// Create synced timeline
		// const tl = createTimeline({
		// 	defaults: {
		// 		ease: "easeOutCubic",
		// 		duration: 400,
		// 	},
		// });
		// tl.add(
		// 	this.props.scrollListContainerRef,
		// 	{
		// 		//@ts-ignore
		// 		scrollTop: this.props.scrollListContainerRef.current?.scrollHeight,
		// 	},
		// 	0,
		// );

		//TODO: we should request chat box list scroller to scroll to the bottom.
		/**
		 * Vì một khi request trigger scroll thì mình sẽ ưu tiên request mới nhất trước.
		 * Dẫn đến, phải có cơ chế abort hoặc skip animation lại để đảm bảo trải nghiệm mượt mà nhất.
		 * Một khi đã request scroll thì sẽ thực thi scroll tới đó, cho nên animation engine phải có cơ chế tự động cancel.
		 */

		/**
		 * NOTE: Thằng React nó recommend là sử dụng scrollOffsetHeight để scroll tới đó.
		 * Refs: https://legacy.reactjs.org/docs/react-component.html#getsnapshotbeforeupdate:~:text=class%20ScrollingList%20extends,%3B%0A%20%20%7D%0A%7D
		 */

		const tween = (
			start: number,
			end: number,
			duration: number,
			onUpdate: (x: number) => void,
		) => {
			const startTime = performance.now();
			const animate = (currentTime: number) => {
				const elapsed = currentTime - startTime;
				const progress = Math.min(elapsed / duration, 1); // từ 0 đến 1
				const value = start + (end - start) * progress; // linear interpolation
				onUpdate(value);
				if (progress < 1) {
					this.rafLor_ = requestAnimationFrame(animate);
				}
			};
			this.rafLor_ = requestAnimationFrame(animate);
		};

		// tween(0, 100, 1000, (x) => {
		// 	console.log(`Current X: ${x}`);
		// });

		if (this.rafLor_) {
			cancelAnimationFrame(this.rafLor_);
			//NOTE: apply luôn target scroll top để tránh bị giật view ko nhỉ?
		}

		// if (snapshot && snapshot.prevScrollHeight) {
		// const currentScrollHeight = this.props.containerRef.current?.scrollHeight ?? 0;
		const chatBoxListView = window.document.getElementById(this.props.scrollerElementId);
		// const chatBoxListView = this.props.controller.scroller.domElement;
		if (
			chatBoxListView &&
			prevProps.state.messages.length !== this.props.state.messages.length
		) {
			const currentListViewHeight = chatBoxListView.getBoundingClientRect().height;
			const currentScrollHeight = chatBoxListView.scrollHeight;
			const currentScrollTop = chatBoxListView.scrollTop;
			const targetScrollTop = currentScrollHeight - currentListViewHeight; //300
			if (targetScrollTop !== currentScrollTop) {
				const offset = Math.abs(targetScrollTop - currentScrollTop)
				const startScrollTop =
					offset > 200
						? targetScrollTop - 200
						: currentScrollTop;
				$Logger.info(
					"dongmt scroll from: ",
					startScrollTop,
					" to: ",
					targetScrollTop,
					" offset: ",
					targetScrollTop - startScrollTop,
				);
				chatBoxListView.scrollTop = startScrollTop;
				chatBoxListView.scrollTo({
					top: targetScrollTop,
					behavior: "smooth",
				});
				// tween(startScrollTop, targetScrollTop, 200, (x) => {
				// 	console.log("dongmt ChatBoxList tween >>ON-UPDATE<<: ", x);
				// 	chatBoxListView.scrollTop = x;
				// });
			}
		}
		// }

		//Trick lỏ...
		// if (this.rafLor_) {
		// 	cancelAnimationFrame(this.rafLor_);
		// }
		// this.rafLor_ = requestAnimationFrame(() => {
		// 	this.rafLor_ = null;
		// 	this.props.controller.scroller.scrollTop = this.props.controller.scroller.scrollHeight;
		// });

		// Thằng này bị chậm frame.
		// setTimeout(() => {
		// 	this.props.scroller.scrollTop = this.props.scroller.scrollHeight;
		// }, 0);
		// const tl = createTimeline({
		// 	defaults: {
		// 		ease: "easeOutCubic",
		// 		duration: 400,
		// 	},
		// 	onUpdate: (...args) => {
		// 		console.log("dongmt ChatBoxList tl >>ON-UPDATE<<", args);
		// 	},
		// });
		// tl.play();

		this.readLastMessagesIfNeeded(prevProps);
	}

	public readLastMessagesIfNeeded(prevProps: Readonly<ChatBoxListProps>) {
		const prevMessages = prevProps.state.messages;
		const currentMessages = this.props.state.messages;
		if (prevMessages !== currentMessages && currentMessages.length) {
			this.props.controller.readLastMessages(currentMessages[currentMessages.length - 1]);
		}
	}

	public componentWillUnmount() {
		$Logger.info("ChatBoxList call componentWillUnmount");
		//TODO: clear side effects, cancel requests, etc... that related to this component.
	}

	static ClassNames = ClassNames;
}
